import { observable } from "@legendapp/state";
import { getDeviceBrowserReport } from "@/apis/clients/reportingClient";
import {
  createRequestGuard,
  reportScopeKey,
  toErrorMessage,
} from "@/stores/storeHelpers";
import type { PerformanceReportRow, ReportScope } from "@/types/reporting";

interface DeviceBrowserState {
  rows: PerformanceReportRow[];
  isLoading: boolean;
  error: string | null;
  loadedForScopeKey: string | null;
}

const DEFAULT_VALUES: DeviceBrowserState = {
  rows: [],
  isLoading: false,
  error: null,
  loadedForScopeKey: null,
};

export const DeviceBrowserStore$ = observable(DEFAULT_VALUES);

const getScopeKey = (scope: ReportScope) =>
  `device-browser:${reportScopeKey(scope)}`;

const guard = createRequestGuard();

export const fetchDeviceBrowserReport = async (scope: ReportScope) => {
  const scopeKey = getScopeKey(scope);
  const result = guard.check(
    scopeKey,
    DeviceBrowserStore$.loadedForScopeKey.get()
  );
  if (result.outcome === "cached") {
    DeviceBrowserStore$.assign({ error: null, isLoading: false });
    return;
  }
  if (result.outcome === "in-flight") return;
  const { requestId } = result;
  DeviceBrowserStore$.assign({ isLoading: true, error: null });

  try {
    const response = await getDeviceBrowserReport(scope);
    if (guard.isStale(requestId)) return;
    guard.clearPending(scopeKey);
    DeviceBrowserStore$.assign({
      rows: response.results,
      loadedForScopeKey: scopeKey,
    });
  } catch (error) {
    if (guard.isStale(requestId)) return;
    guard.clearPending(scopeKey);
    DeviceBrowserStore$.error.set(toErrorMessage(error));
  } finally {
    if (!guard.isStale(requestId)) {
      DeviceBrowserStore$.isLoading.set(false);
    }
  }
};

export const clearDeviceBrowserReport = () => {
  guard.invalidate();
  DeviceBrowserStore$.assign({
    rows: [],
    isLoading: false,
    loadedForScopeKey: null,
    error: null,
  });
};
